import { useCallback } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { Module } from "@/constants";
import { fetchAgreement } from "@/services/agreements";

/**
 * Ciche odświeżenie szczegółów umowy (ASMMNT-83, etap 2): pobiera dane poza stanem zapytania
 * i wpisuje je `setQueryData`. `invalidateQueries` ustawiłoby `isFetching`, a `useAgreementLoading`
 * zamienia to w loader całej strony, który odmontowuje sekcje (gubi stan, przerywa wsad plików).
 * Gdy pobranie się nie uda (sieć, 404 umowy spoza struktury), wracamy do invalidacji: loader
 * i ekran błędu są wtedy właściwą odpowiedzią.
 */
export const silentlyRefreshAgreement = (
  queryClient: QueryClient,
  agreementId: string,
): Promise<void> => {
  const queryKey = [Module.Agreements, agreementId];
  return fetchAgreement(agreementId)
    .then((details) => {
      queryClient.setQueryData(queryKey, details);
    })
    .catch(async () => {
      await queryClient.invalidateQueries({ queryKey });
    });
};

/** `silentlyRefreshAgreement` związane z klientem zapytań; stabilne per umowa. */
export const useSilentAgreementRefresh = (agreementId: string) => {
  const queryClient = useQueryClient();
  return useCallback(
    () => silentlyRefreshAgreement(queryClient, agreementId),
    [queryClient, agreementId],
  );
};
